import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import "./HelpfulFeedback.scss";
import likeIcon from "../../../../assets/VZY/like-light.svg";
import dislikeIcon from "../../../../assets/VZY/Dislike-light.svg";
import afterLike from "../../../../assets/VZY/afterLike.svg";
import uploadFile from "../../../../assets/VZY/upload-file.svg";
import { useSearchParams } from "react-router-dom";

export const HelpfulFeedback = ({
  title,
  contentID,
  contentCode,
  fetchIDsUsed,
}) => {
  const apiBaseURL = process.env.REACT_APP_API_URL || window.location.origin;
  const REDIRECT_URL = "https://vzy.one/en/account";

  const [searchParams] = useSearchParams();
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disLikeMessage, setDisLikeMessage] = useState(
    `This wasn't helpful. Chat with us to get it resolved.`,
  );

  const initialForm = {
    email: "",
    subject: "",
    description: "",
    file: null,
  };

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const getSessionData = useCallback(() => {
    try {
      return JSON.parse(sessionStorage.getItem("helpSupportData") || "{}");
    } catch {
      return {};
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setForm((prev) => ({ ...prev, file: null }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.file;
      return newErrors;
    });
  }, []);

  useEffect(() => {
    const urlToken = searchParams.get("a_t");
    const urlSid = searchParams.get("sid");
    const urlMno = searchParams.get("mno");

    if (urlToken && urlSid && urlMno) {
      sessionStorage.setItem(
        "helpSupportData",
        JSON.stringify({
          token: urlToken,
          sid: urlSid,
          mno: urlMno,
        }),
      );
      return;
    }

    const storedData = getSessionData();
    const isTokenEmpty = !storedData?.token?.trim();
    const isSidEmpty = !storedData?.sid?.trim();
    const isSidZero = Number(storedData?.sid) === 0;
    const isMnoEmpty = !storedData?.mno?.trim();

    if (isTokenEmpty || isSidEmpty || isMnoEmpty || isSidZero) {
      window.location.replace(REDIRECT_URL);
    }
  }, [searchParams, getSessionData]);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    } else if (form.email.length > 50) {
      newErrors.email = "Email must not exceed 50 characters";
    }

    if (!form.subject) {
      newErrors.subject = "Subject is required";
    } else if (form.subject.trim().length < 5) {
      newErrors.subject = "Subject must be at least 5 characters";
    } else if (form.subject.length > 100) {
      newErrors.subject = "Subject must not exceed 100 characters";
    }

    if (!form.description) {
      newErrors.description = "Description is required";
    } else if (form.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (form.description.length > 500) {
      newErrors.description = "Description must not exceed 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const validateAndSetFile = useCallback((file) => {
    if (!file) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.file;
        return newErrors;
      });
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only JPG and PNG images are allowed",
      }));
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    if (file.size > 250 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: `File must be under 250KB (current: ${Math.round(file.size / 1024)}KB)`,
      }));
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.file;
      return newErrors;
    });
    setForm((prev) => ({ ...prev, file }));
  }, []);

  const handleFileBrowse = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      validateAndSetFile(file);
    },
    [validateAndSetFile],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      validateAndSetFile(file);
    },
    [validateAndSetFile],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setErrors({});
  }, [initialForm]);

  const showToastMessage = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  const submitLike = useCallback(async () => {
    if (loading) return;

    try {
      setLoading(true);
      const sessionData = getSessionData();

      const payload = {
        ottSMSID: sessionData?.sid || "",
        contentCode: contentCode || "0",
        isLike: true,
        createdBy: 1,
      };

      await axios.post(
        `${apiBaseURL}/api/VZYHelpSupport/ContentReactionLog`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      setFeedback("like");
    } catch (error) {
      console.error("Like submission error:", error);
      showToastMessage("Failed to submit feedback");
    } finally {
      setLoading(false);
    }
  }, [apiBaseURL, contentCode, loading, getSessionData, showToastMessage]);

  const submitDislike = useCallback(async () => {
    if (!validate() || loading) return;

    try {
      setLoading(true);
      const sessionData = getSessionData();

      const formData = new FormData();
      formData.append("OttSMSId", sessionData?.sid || "");
      formData.append("Category", fetchIDsUsed?.categoryName || "");
      formData.append("SubCategory", form.subject);
      formData.append("MailForm", form.email);
      formData.append("MailId", form.email);
      formData.append("Subject", form.subject);
      formData.append("ComplainDetails", form.description);
      formData.append("LinkURL", window.location.href);
      formData.append("CategoryId", fetchIDsUsed?.categoryID || "");
      formData.append("ParentCategoryId", fetchIDsUsed?.categoryID || "");
      formData.append("SMSToken", sessionData?.token || "");

      if (form.file) {
        formData.append("File", form.file);
      }

      const response = await axios.post(
        `${apiBaseURL}/api/v1/ContactUs/VZYComplainAndFeedback`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const message = response?.data?.resultMessage || "Submitted successfully";
      showToastMessage(message);
      setDisLikeMessage(message);
      setFeedback("dislike");
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Dislike submission error:", error);
      showToastMessage(
        error?.response?.data?.resultMessage ||
          "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [
    validate,
    loading,
    getSessionData,
    fetchIDsUsed,
    form,
    apiBaseURL,
    showToastMessage,
    resetForm,
  ]);

  useEffect(() => {
    document.body.style.overflow = showModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showModal]);

  return (
    <>
      {showToast && (
        <div className="feedback-toast-success">
          <img src={afterLike} alt="Success" />
          {toastMessage}
        </div>
      )}

      <div className="helpful-wrapper">
        {!feedback && (
          <>
            <span className="helpful-text">{title}</span>

            <div className="helpful-actions">
              <button
                className="icon-btn"
                onClick={submitLike}
                disabled={loading}
                aria-label="Like"
              >
                <img src={likeIcon} alt="Like" />
              </button>

              <button
                className="icon-btn"
                onClick={() => setShowModal(true)}
                disabled={loading}
                aria-label="Dislike"
              >
                <img src={dislikeIcon} alt="Dislike" />
              </button>
            </div>
          </>
        )}

        {feedback === "like" && (
          <span className="feedback-success great">
            <img src={afterLike} alt="Liked" />
            Great! We're happy we could assist you.
          </span>
        )}

        {feedback === "dislike" && (
          <p className="feedback-success sorry">{disLikeMessage}</p>
        )}
      </div>

      {showModal && (
        <div className="feedback-modal" role="dialog" aria-modal="true">
          <div className="modal-form-box">
            <button
              className="closeModal-btn"
              onClick={() => {
                resetForm();
                setShowModal(false);
              }}
              aria-label="Close modal"
              type="button"
            >
              ×
            </button>

            <h4>Write to Us</h4>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitDislike();
              }}
            >
              <div className="feedback-row">
                {/* Email */}
                <div className="feedback-col1">
                  <input
                    type="email"
                    name="email"
                    maxLength={50}
                    placeholder="Email ID*"
                    value={form.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.email && <p className="error-text">{errors.email}</p>}
                </div>

                <div className="feedback-col1">
                  <input
                    type="text"
                    name="subject"
                    maxLength={100}
                    placeholder="Subject*"
                    value={form.subject}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.subject && (
                    <p className="error-text">{errors.subject}</p>
                  )}
                </div>

                <div className="feedback-col2">
                  <input
                    type="text"
                    disabled
                    name="category"
                    placeholder="Category*"
                    value={
                      fetchIDsUsed?.categoryName ||
                      fetchIDsUsed?.categoryID ||
                      ""
                    }
                  />
                </div>

                <div className="feedback-col2">
                  <textarea
                    name="description"
                    maxLength={500}
                    placeholder="Description*"
                    value={form.description}
                    onChange={handleChange}
                    disabled={loading}
                    rows={5}
                  />
                  {errors.description && (
                    <p className="error-text">{errors.description}</p>
                  )}
                </div>

                <div
                  className="file-drop-zone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  <div>
                    {form.file ? (
                      <div className="file-uploaded">
                        <p className="file-name">{form.file.name}</p>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="remove-file-btn"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="upload-icon">
                          <img src={uploadFile} alt="Upload" />
                        </div>
                        <label className="browse-link">
                          Browse
                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleFileBrowse}
                            disabled={loading}
                          />
                        </label>
                        <span className="drag-here"> or drag files here</span>
                        <p className="file-hint">Max 250KB (JPG, PNG only)</p>
                      </>
                    )}
                    {errors.file && <p className="error-text">{errors.file}</p>}
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="modal-submit"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
